<template>
  <v-dialog v-model="dialog" width="90vw" content-class="rounded-20">
    <v-card flat class="rounded-20" height="70vh">
      <v-card-title class="title-font"> SCANS &amp; FILES </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="2">
            <v-list
              dense
              class="transparent pa-0"
              v-for="(category, index) in categories"
              :key="index"
            >
              <v-list-item
                class="mb-2 custom-grey"
                :input-value="selectedCategory === category.value"
                @click="selectedCategory = category.value"
              >
                <v-list-item-content class="title-font">
                  <v-list-item-title class="text-wrap">
                    {{ category.title }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-wrap">
                    {{ category.subtitle }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-chip
                  small
                  :color="fileCount(category.value) > 0 ? 'primary' : ''"
                  class="rounded-10"
                >
                  {{ fileCount(category.value) }}
                </v-chip>
              </v-list-item>
            </v-list>
          </v-col>
          <v-col cols="10">
            <v-card
              flat
              class="rounded-10 overflow-y-auto custom-grey"
              max-height="65vh"
            >
              <v-card-text>
                <v-row v-if="!filteredFiles.length && loading">
                  <v-col cols="3" v-for="index in 4" :key="index">
                    <v-skeleton-loader type="image" />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col
                    cols="12"
                    class="text-center"
                    v-if="!filteredFiles.length && !loading"
                  >
                    No files found
                  </v-col>
                  <v-col
                    cols="3"
                    v-for="(file, index) in filteredFiles"
                    :key="index"
                  >
                    <v-card
                      flat
                      class="rounded-10 d-flex flex-column"
                      height="100%"
                      v-if="!loading"
                    >
                      <v-toolbar
                        dark
                        color="primary"
                        flat
                        dense
                        class="rounded-xl rounded-b-0"
                      >
                        {{ file.session_type }}
                        <v-spacer />
                        <v-tooltip bottom v-if="file.return_file">
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              color="warning"
                              v-bind="attrs"
                              v-on="on"
                              class="ms-1"
                            >
                              mdi-shield-refresh
                            </v-icon>
                          </template>
                          <span>Return File</span>
                        </v-tooltip>
                      </v-toolbar>
                      <v-row align="center">
                        <v-col>
                          <v-img
                            v-if="fileType(file) === 'image'"
                            height="150"
                            contain
                            :src="file.url"
                          />
                          <ModelViewer
                            v-if="fileType(file) === 'model'"
                            :filename="file.file_name"
                            :url="file.url"
                            :height="150"
                            :width="270"
                          />
                          <v-card
                            v-if="
                              fileType(file) === 'download' ||
                              fileType(file) === 'html'
                            "
                            flat
                            max-height="150"
                          >
                            <v-card-text class="text-center">
                              <v-icon>mdi-information</v-icon>
                              Cannot Preview File
                            </v-card-text>
                          </v-card>
                        </v-col>
                      </v-row>
                      <v-card-text class="text-center">
                        <p class="off rounded-10">
                          {{ formatOFTFilename(file.file_name) }}
                        </p>
                        <v-btn
                          v-if="fileSelected(file)"
                          small
                          class="btn-success"
                          @click="handleSelection(file)"
                          disabled
                        >
                          Selected
                        </v-btn>
                        <v-btn
                          v-else
                          small
                          class="btn-primary"
                          @click="handleSelection(file)"
                        >
                          Select
                        </v-btn>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import { formatOFTFilename, fileType } from "@/utils"
import client from "@/lib/ApiClient"
import ModelViewer from "@/components/shared/ModelViewer.vue"
import fileCategories from "@/components/shared/file_categories"

export default {
  props: ["value", "category", "open", "projectFileType", "selectedFiles"],

  components: { ModelViewer },

  data() {
    return {
      loading: false,
      selectedCategory: "",
      categoryItems: Object.values(fileCategories)
    }
  },

  computed: {
    ...mapGetters(["selectedOrder", "selectedOrderSession", "orderFiles"]),

    files() {
      return this.orderFiles
    },

    categories() {
      return [
        { title: "All Uncategorized Files", subtitle: "", value: "" },
        this.categoryItems.find((category) => category.value === this.category)
      ]
    },

    filteredFiles() {
      if (!this.selectedCategory) {
        return this.files
          .filter((file) => !file.file_category)
          .map((file) => {
            return {
              ...file,
              session_type: this.selectedOrder.sessions.find(
                (session) => session.uid === file.order_session_uid
              ).session_type
            }
          })
      }
      return this.files
        .filter((file) => file.file_category === this.selectedCategory)
        .map((file) => {
          return {
            ...file,
            session_type: this.selectedOrder.sessions.find(
              (session) => session.uid === file.order_session_uid
            ).session_type
          }
        })
    },

    dialog: {
      get() {
        return this.open
      },
      set(value) {
        this.$emit("close", value)
      }
    }
  },

  methods: {
    ...mapActions(["triggerReload"]),

    formatOFTFilename,

    fileType,

    fileCount(category) {
      return this.files.filter((file) => file.file_category === category).length
    },

    async handleSelection(file) {
      if (
        !["model", "image"].includes(this.fileType(file)) &&
        this.projectFileType !== "other"
      ) {
        return
      }
      await client.instance.orders.updateFileCategory(
        this.selectedOrder.uid,
        file.order_session_uid,
        file.uid,
        this.category
      )
      this.$emit("input", file)
    },

    fileSelected(file) {
      if (this.selectedFiles?.length) {
        if (this.selectedFiles.find((f) => f.uid === file.uid)) return true
        return false
      } else if (this.value && file.uid === this.value.uid) return true
      return false
    }
  },

  mounted() {
    this.triggerReload("files")
    this.selectedCategory = this.category
  }
}
</script>
